/**
 * Reads a course's look-and-feel document for the maps.
 */
import {doc, getFirestore, type DocumentReference} from 'firebase/firestore';
import {useMemo} from 'react';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {
  DEFAULT_LOOK_AND_FEEL,
  parseLookAndFeel,
  type LookAndFeel,
} from './lookAndFeelSchema';

/**
 * The Firestore reference of a course's look-and-feel document.
 *
 * @param courseId - The course.
 * @return The document reference.
 */
export function lookAndFeelRef(courseId: string): DocumentReference {
  return doc(
    getFirestore(),
    'roadmap',
    courseId,
    'descriptions',
    'lookAndFeel',
  );
}

/**
 * Subscribes to a course's look and feel.
 *
 * @param courseId - The course.
 * @return The parsed look and feel (defaults until loaded or when absent)
 *     and whether the first snapshot is still pending.
 */
export function useLookAndFeel(courseId: string): {
  lookAndFeel: LookAndFeel;
  isLoading: boolean;
} {
  const ref = useMemo(() => lookAndFeelRef(courseId), [courseId]);
  const [data, isLoading] = useDocumentData(ref);
  const lookAndFeel = useMemo(
    () => (data === undefined ? DEFAULT_LOOK_AND_FEEL : parseLookAndFeel(data)),
    [data],
  );
  return {lookAndFeel, isLoading};
}
